import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { UserLink } from '../user-link/user-link';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  takeUntil,
  filter,
  take,
  switchMap,
  catchError,
  of,
  map,
} from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { UserService } from '../../../services/user.service';
import { ConversationService } from '../../../services/conversation.service';
import { MessagingService } from '../../../services/messaging.service';
import {
  Chat,
  Message,
  MessageAttachment,
  MessageSearchResult,
} from '../../../models/message.model';
import { NewConversationDialogComponent } from '../new-conversation-dialog/new-conversation-dialog';
import { ChatService } from '../../../services/chat.service';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { ConfirmCancelDialog } from '../confirm-cancel-dialog/confirm-cancel-dialog';
import { AttachmentPreviewDialog } from '../attachment-preview-dialog/attachment-preview-dialog';
import { HasPermissionDirective } from '../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../constants/permissions';
import { saveBlobAsFile } from '../../../helpers/file-download';
import { UserAvatar } from '../user-avatar/user-avatar';

/** Types allowed by the server's message-attachments endpoint — kept in
 *  sync with ALLOWED_ATTACHMENT_MIME_TYPES in message.controller.js. */
const ALLOWED_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const MAX_ATTACHMENTS_PER_MESSAGE = 5;

interface PendingAttachment {
  file: File;
  status: 'uploading' | 'done' | 'error';
  attachment?: MessageAttachment;
}

@Component({
  selector: 'app-messages-thread-page',
  standalone: true,
  imports: [
    UserLink,
    CommonModule,
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDividerModule,
    FormsModule,
    MatAutocompleteModule,
    HighlightPipe,
    HasPermissionDirective,
    UserAvatar,
  ],
  templateUrl: './messages-thread-page.html',
  styleUrls: ['./messages-thread-page.css'],
})
export class MessagesThreadPage implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('inputAreaRef') inputAreaRef?: ElementRef<HTMLElement>;
  @ViewChild('membersPanelListRef') membersPanelListRef?: ElementRef<HTMLElement>;
  @ViewChild('attachmentInput') attachmentInputRef!: ElementRef<HTMLInputElement>;

  readonly attachmentAccept = ALLOWED_ATTACHMENT_ACCEPT;
  readonly maxAttachments = MAX_ATTACHMENTS_PER_MESSAGE;
  readonly permissions = USER_PERMISSIONS;
  pendingAttachments: PendingAttachment[] = [];
  isDraggingOver = false;
  private dragDepth = 0;
  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private userService: UserService,
    private messagingService: MessagingService,
    private cdr: ChangeDetectorRef,
    private chatService: ChatService,
    private conversationService: ConversationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  currentMatchIndex = 0;
  private pendingDeepLinkConversationId: number | null = null;
  matchNavDismissed = false;

  newMessage = '';
  selectedChat: Chat | null = null;
  filteredChats: Chat[] = [];
  searchQuery = '';
  currentUserId!: number;

  editingMessageId: number | null = null;
  editMessageText = '';

  replyingToMessage: Message | null = null;

  editingConversationName = false;
  conversationNameDraft = '';

  onlineUserIds = new Set<number>();

  private shouldJump = false;
  private lastScrolledChatId: number | null = null;
  isNearBottom = true;
  showJumpToLatest = false;
  // Where the user was scrolled when they last left a conversation this
  // session, keyed by conversation id. Overwritten every time that
  // conversation is left again, so it always reflects the most recent
  // visit. Lives in component memory only — reset on reload/navigating
  // away from this page, not persisted.
  private lastScrollPositionByChat = new Map<number, { scrollTop: number; wasAtBottom: boolean }>();
  private inputAreaResizeObserver?: ResizeObserver;
  private observedInputAreaEl?: HTMLElement;
  private pendingJumpMessageId: number | null = null;
  highlightedMessageId: number | null = null;
  private searchSubject = new Subject<string>();
  // Fed on every "still typing" signal; once it goes quiet for the debounce
  // window, we tell the other participant(s) typing has stopped — covers
  // the case where the user just abandons the input without clearing it or
  // sending, which an empty-input/send-triggered clear alone would miss.
  private typingStoppedSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private socketListenersSetup = false;
  isSearching = false;
  chats: Chat[] = [];
  private contentMatches: MessageSearchResult[] = [];

  // Tracks the most recent edit that hasn't been confirmed yet, so its text
  // can be restored if the server rejects it (e.g. profanity) instead of
  // being lost when the input is optimistically cleared. Sends have their
  // own equivalent via chatService.sendFailed$, since they go over REST
  // rather than the socket.
  private lastPendingAction: { type: 'edit'; messageId: number; text: string } | null = null;

  // Function sets up the title of the tab, sets up the search bar and its parameters, and also connects the user to the sockets for the messages
  ngOnInit(): void {
    this.title.setTitle('Messages | Handle');
    this.setupSearch();
    this.setupTypingDebounce();
    this.setupQueryParams();

    this.chatService.conversations$.pipe(takeUntil(this.destroy$)).subscribe((chats) => {
      this.chats = chats;
      if (this.searchQuery.trim()) {
        this.filteredChats = this.getFilteredAndSortedChats(this.searchQuery);
      }
      this.reconcileOpenConversation();
      this.cdr.detectChanges();
    });

    this.chatService.selectedChat$.pipe(takeUntil(this.destroy$)).subscribe((chat) => {
      const isNewChatSelection = chat?.id !== this.lastScrolledChatId;

      // Snapshot where we're leaving off, before selectedChat/scrollContainer
      // get reassigned to the incoming chat below.
      if (this.selectedChat && this.selectedChat.id !== chat?.id) {
        this.saveScrollPosition(this.selectedChat.id);
      }

      this.selectedChat = chat;
      this.messagingService.setVisibleConversation(chat?.id ?? null);
      if (chat) this.reconcileOpenConversation();

      if (isNewChatSelection) {
        // Switching conversations (as opposed to a live update to the one
        // already open) should drop any in-progress rename — otherwise the
        // input keeps floating in the header with stale text, and saving it
        // would rename whichever chat is now selected instead.
        this.cancelEditConversationName();
      }

      // All template-bound state (showJumpToLatest, isNearBottom, etc.) is
      // finalized before the one detectChanges() call below — mutating it
      // afterward, in the same synchronous callback, is what
      // ExpressionChangedAfterItHasBeenCheckedError (NG0100) catches.
      let scrollSmooth = true;
      let shouldScrollNow = false;
      let restoreScrollTop: number | null = null;

      if (chat) {
        if (this.pendingJumpMessageId !== null) {
          this.shouldJump = true;
        } else if (isNewChatSelection) {
          this.lastScrolledChatId = chat.id;
          const saved = this.lastScrollPositionByChat.get(chat.id);

          if (saved && !saved.wasAtBottom) {
            // Reading history when they left this conversation — put them
            // back where they were instead of yanking them down to the
            // bottom again.
            this.isNearBottom = false;
            this.showJumpToLatest = true;
            restoreScrollTop = saved.scrollTop;
          } else {
            // Either never visited this session, or was caught up at the
            // bottom when they left — jump straight to the (current)
            // bottom, no animation to watch.
            this.isNearBottom = true;
            this.showJumpToLatest = false;
            shouldScrollNow = true;
            scrollSmooth = false;
          }
        } else if (this.isNearBottom) {
          // Deliberately instant, not smooth: a `smooth` scrollTo() fires
          // one native `scroll` event per animation frame, but the
          // suppression below only ever covers a single event — so an
          // animated correction here could have several of its own
          // in-flight frames misread as "the user scrolled away"
          // mid-animation, intermittently landing short of the bottom.
          shouldScrollNow = true;
          scrollSmooth = false;
        } else {
          // A new message arrived while the user was scrolled up reading
          // older messages — don't yank them down, just surface the button.
          this.showJumpToLatest = true;
        }
      }

      this.cdr.detectChanges();

      if (restoreScrollTop !== null) {
        this.restoreScrollPosition(restoreScrollTop);
      } else if (shouldScrollNow) {
        this.scrolltoBottom(scrollSmooth);
      }
    });

    this.chatService.onlineUserIds$.pipe(takeUntil(this.destroy$)).subscribe((ids) => {
      this.onlineUserIds = ids;
      this.cdr.detectChanges();
    });

    this.messagingService
      .onMessageError()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.lastPendingAction) return;

        if (this.lastPendingAction.type === 'edit' && this.editingMessageId === null) {
          this.editingMessageId = this.lastPendingAction.messageId;
          this.editMessageText = this.lastPendingAction.text;
        }

        this.lastPendingAction = null;
        this.cdr.detectChanges();
      });

    this.chatService.sendFailed$.pipe(takeUntil(this.destroy$)).subscribe((text) => {
      if (!this.newMessage.trim()) {
        this.newMessage = text;
        this.cdr.detectChanges();
      }
    });

    this.userService
      .getCurrentUser()
      .pipe(
        filter((user) => !!user && !!user.id),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((user) => {
        this.currentUserId = user.id;
        this.chatService.initializeSocketListeners(this.currentUserId);
        this.chatService.reloadConversations(this.currentUserId);

        this.messagingService.requestPresenceList();
      });
  }

  isUserOnline(userId?: number | null): boolean {
    return !!userId && this.onlineUserIds.has(Number(userId));
  }

  getSenderName(message: Message): string {
    const sender = message.sender;

    if (!sender) {
      return '';
    }

    return `${sender.preferredName ?? sender.first_name ?? ''} ${sender.last_name ?? ''}`.trim();
  }

  getReplyQuoteSenderName(replyTo: NonNullable<Message['replyTo']>): string {
    const sender = replyTo.sender;

    if (!sender) {
      return '';
    }

    return `${sender.preferredName ?? sender.first_name ?? ''} ${sender.last_name ?? ''}`.trim();
  }

  /** Matches the '📎 Attachment' fallback already used for attachment-only
   *  messages in the conversation list preview and the send-broadcast
   *  preview payload, for a consistent look across the app. */
  getReplyQuoteSnippet(replyTo: NonNullable<Message['replyTo']>): string {
    if (replyTo.is_deleted) {
      return 'This message was deleted';
    }

    const content = replyTo.content?.trim();

    if (!content) {
      return '📎 Attachment';
    }

    return content.length > 80 ? `${content.slice(0, 80)}…` : content;
  }

  getReadReceiptSummary(message: Message): string {
    const receipts = message.MessageReadReceipts;

    if (!receipts?.length) {
      return '';
    }

    const names = receipts
      .map((receipt) => receipt.reader?.preferredName ?? receipt.reader?.first_name)
      .filter((name): name is string => !!name);

    if (names.length === 1) {
      return `Seen by ${names[0]}`;
    }

    if (names.length === 2) {
      return `Seen by ${names[0]} and ${names[1]}`;
    }

    return `Seen by ${names[0]}, ${names[1]} +${names.length - 2}`;
  }

  /**
   * Names whoever is currently typing in a chat, resolved from
   * typingUserIds via that chat's members list — works the same for direct
   * and group conversations, since a direct chat's members array already
   * includes the other participant. Returns '' when nobody is typing.
   * @param {Chat} chat - The chat to summarize typing activity for.
   * @returns {string}
   */
  getTypingLabel(chat: Chat): string {
    const ids = chat.typingUserIds ?? [];

    if (!ids.length) {
      return '';
    }

    const names = ids
      .map((id) => chat.members?.find((member) => member.id === id)?.name)
      .filter((name): name is string => !!name);

    if (!names.length) {
      return '';
    }

    if (names.length === 1) {
      return `${names[0]} is typing...`;
    }

    if (names.length === 2) {
      return `${names[0]} and ${names[1]} are typing...`;
    }

    return `${names[0]}, ${names[1]} +${names.length - 2} are typing...`;
  }

  startEditMessage(message: Message): void {
    this.editingMessageId = message.id;
    this.editMessageText = message.content;
  }

  startReply(message: Message): void {
    if (message.is_deleted || message.is_system) return;
    this.replyingToMessage = message;
  }

  cancelReply(): void {
    this.replyingToMessage = null;
  }

  /**
   * Scrolls to and briefly highlights a message elsewhere in the thread —
   * used both by clicking a quoted reply snippet and (already) by jumping
   * to a search match.
   */
  jumpToMessage(messageId: number): void {
    this.pendingJumpMessageId = messageId;
    this.shouldJump = true;
  }

  cancelEditMessage(): void {
    this.editingMessageId = null;
    this.editMessageText = '';
  }

  saveEditedMessage(message: Message): void {
    const newContent = this.editMessageText.trim();

    if (!newContent || newContent === message.content) {
      this.cancelEditMessage();
      return;
    }
    if (!this.selectedChat?.userId) return;

    this.lastPendingAction = { type: 'edit', messageId: message.id, text: newContent };
    this.messagingService.editMessage(message.id, message.conversation_id, newContent);
    this.cancelEditMessage();
  }

  /**
   * Whether the current user can rename the currently open conversation —
   * only the group's creator, and only for group chats (direct chats have
   * no user-editable name).
   * @returns {boolean}
   */
  canRenameConversation(): boolean {
    return !!this.selectedChat?.is_group && this.selectedChat.createdBy === this.currentUserId;
  }

  startEditConversationName(): void {
    if (!this.selectedChat || !this.canRenameConversation()) return;

    this.conversationNameDraft = this.selectedChat.name;
    this.editingConversationName = true;
  }

  cancelEditConversationName(): void {
    this.editingConversationName = false;
    this.conversationNameDraft = '';
  }

  saveConversationName(): void {
    if (!this.editingConversationName || !this.selectedChat) return;

    const trimmedName = this.conversationNameDraft.trim();

    if (!trimmedName || trimmedName === this.selectedChat.name) {
      this.cancelEditConversationName();
      return;
    }

    this.chatService.renameConversation(this.selectedChat.id, trimmedName, this.currentUserId);
    this.cancelEditConversationName();
  }

  private reloadConversations(): void {
    this.chatService.reloadConversations(this.currentUserId);
  }

  openAddMembersDialog(): void {
    if (!this.selectedChat?.is_group) {
      return;
    }

    const existingMemberIds = this.selectedChat.members?.map((member) => member.id) || [];

    const dialogRef = this.dialog.open(NewConversationDialogComponent, {
      width: '400px',
      data: {
        mode: 'add-members',
        currentUserId: this.currentUserId,
        existingMemberIds,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.selectedUserIds?.length) {
        return;
      }

      this.chatService.addGroupMembers(
        this.selectedChat!.id,
        result.selectedUserIds,
        this.currentUserId,
      );
    });
  }

  /**
   * Fetches a plain-text transcript of the open conversation and saves it as
   * a file. Gated behind MESSAGING.EXPORT_TRANSCRIPT — the download button
   * itself is hidden without that permission, but the request would also be
   * rejected server-side.
   */
  downloadTranscript(): void {
    if (!this.selectedChat) return;

    const chat = this.selectedChat;
    this.conversationService.downloadTranscript(chat.id).subscribe({
      next: (blob) => saveBlobAsFile(blob, this.buildTranscriptFilename(chat)),
      error: () => this.snackBar.open('Failed to download transcript.', 'Dismiss', { duration: 5000 }),
    });
  }

  private buildTranscriptFilename(chat: Chat): string {
    const safeName = chat.name.replace(/[^a-z0-9]+/gi, '_');
    const date = new Date().toISOString().slice(0, 10);
    return `transcript_${safeName}_${date}.txt`;
  }

  /**
   * Opens the window for the list of users that the current user can choose to make a new conversation with, redirects to NewConversationDialogComponent files
   */
  openNewConversationDialog(): void {
    const dialogRef = this.dialog.open(NewConversationDialogComponent, {
      width: '420px',
      data: {
        currentUserId: this.currentUserId,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.reloadConversations();
      }
    });
  }

  // Private function for the initial set up of the search bar in messages
  private setupSearch(): void {
    this.searchSubject
      .pipe(
        // distinctUntilChanged runs before the debounce so it compares every raw
        // keystroke rather than only the debounced ones. If it ran after, clearing
        // the search and retyping the same term within the debounce window would
        // look like a duplicate and get dropped, leaving the results empty.
        distinctUntilChanged(),
        debounceTime(250),
        switchMap((query) =>
          query.trim()
            ? this.conversationService.searchMessages(this.currentUserId, query).pipe(
                catchError(() => of([] as MessageSearchResult[])),
                map((results) => ({ query, results })),
              )
            : of({ query, results: [] as MessageSearchResult[] }),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ query, results }) => {
        this.contentMatches = results;
        this.filteredChats = this.getFilteredAndSortedChats(query);
        this.syncSelectedChatMatches();
        this.isSearching = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Sets up the trailing debounce that automatically tells the other
   * participant(s) typing has stopped once the composer has gone quiet for
   * a few seconds — the leading "started typing" signal is sent immediately
   * from sendTyping() itself, on every keystroke.
   */
  private setupTypingDebounce(): void {
    this.typingStoppedSubject
      .pipe(debounceTime(3000), takeUntil(this.destroy$))
      .subscribe(() => this.sendTyping(false));
  }

  //keeps the open chat's match details in step with the current query.
  private syncSelectedChatMatches(): void {
    if (!this.selectedChat) return;

    const result = this.contentMatches.find(
      (match) => match.conversation_id === this.selectedChat!.id,
    );

    this.chatService.updateSelectedChatMatches({
      matchCount: result?.matchCount,
      matchingMessageIds: result?.matchingMessageIds,
      matchSnippet: result?.snippet ?? undefined,
    });

    const ids = result?.matchingMessageIds ?? [];
    this.currentMatchIndex = ids.length ? Math.min(this.currentMatchIndex, ids.length - 1) : 0;
  }

  private setupQueryParams(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const q = params['q'];
      if (q) {
        this.searchQuery = q;
        this.filteredChats = this.getFilteredAndSortedChats(q);
        this.searchSubject.next(q);
        this.cdr.detectChanges();
      }

      // Notification links deep-link here with ?conversationId=X.
      const conversationId = Number(params['conversationId']);
      if (conversationId) {
        this.pendingDeepLinkConversationId = conversationId;
        this.reconcileOpenConversation();
      }
    });
  }

  toggleMuted(): void {
    if (!this.selectedChat || !this.currentUserId) return;
    this.chatService.setMuted(this.selectedChat, this.currentUserId, !this.selectedChat.muted);
  }

  /**
   * Opens the deep-linked conversation once the list has it, or otherwise makes
   * sure the open conversation is marked read. Waits for the user and list to
   * load, since neither exists yet on first render.
   */
  private reconcileOpenConversation(): void {
    if (!this.currentUserId) return;

    if (this.pendingDeepLinkConversationId !== null) {
      const target = this.chats.find((c) => c.id === this.pendingDeepLinkConversationId);
      if (!target && !this.chats.length) return;

      this.pendingDeepLinkConversationId = null;
      if (target) {
        this.selectChat(target);
        return;
      }
    }

    this.chatService.markSelectedChatRead(this.currentUserId);
  }

  // Search Function Portion
  onSearchInput(query: string): void {
    this.isSearching = true;
    this.searchSubject.next(query);
    this.updateQueryParam(query);
  }

  /**
   * Clears the search box and its results. The empty query is also pushed
   * through the search pipeline so its distinctUntilChanged state is reset —
   * otherwise the next search for the term just cleared would be dropped as a
   * duplicate and silently return nothing.
   * @returns {void}
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.filteredChats = [];
    this.contentMatches = [];
    this.isSearching = false;
    this.searchSubject.next('');
    this.updateQueryParam('');
    this.cdr.detectChanges();
  }

  get visibleChats(): Chat[] {
    return this.searchQuery.trim() ? this.filteredChats : this.chats;
  }

  /**
   * Syncs the search query to the URL as a `q` param without triggering a navigation.
   * @param {string} query - The query string to set or remove.
   * @returns {void}
   */
  private updateQueryParam(query: string): void {
    const url = new URL(window.location.href);
    if (query.trim()) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Returns true when the full name, or any word in it, starts with the query.
   * @param {string} name - The name to test.
   * @param {string} q - The lower-cased search string.
   * @returns {boolean}
   */
  private nameMatches(name: string, q: string): boolean {
    return (
      name
        .toLowerCase()
        .split(' ')
        .some((word) => word.startsWith(q)) || name.toLowerCase().startsWith(q)
    );
  }

  /**
   * Filters chats by whether any word in the chat name or in a member's name
   * starts with the query, then appends chats whose message content matched on
   * the server. Name matches come first — direct chats before groups, each
   * sorted alphabetically; content matches follow by recency. The current user
   * is skipped so searching your own name does not match every conversation.
   * Returns an empty array for empty queries.
   *
   * Names match by prefix but content matches by substring — searching a
   * phrase like "essay draft" only ever works as a substring.
   * @param {string} query - The search string to filter against.
   * @returns {Chat[]}
   */
  private getFilteredAndSortedChats(query: string): Chat[] {
    if (!query.trim()) return [];

    const q = query.toLowerCase();

    const byName = this.chats
      .filter(
        (chat) =>
          this.nameMatches(chat.name, q) ||
          (chat.members ?? []).some(
            (member) => member.id !== this.currentUserId && this.nameMatches(member.name, q),
          ),
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    const byNameDirect = byName.filter((chat) => !chat.is_group);

    const byNameGroup = byName.filter((chat) => chat.is_group);

    const namedIds = new Set(byName.map((chat) => chat.id));

    const byContent = this.contentMatches
      .filter((result) => !namedIds.has(result.conversation_id))
      .map((result) => this.withMatchDetails(result))
      .filter((chat): chat is Chat => chat !== null)
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

    return [...byNameDirect, ...byNameGroup, ...byContent];
  }

  /**
   * Pairs a server search result with its chat, returning a copy decorated with
   * the match details. Returns null when the chat is not loaded.
   * @param {MessageSearchResult} result - A single content match from the server.
   * @returns {Chat | null}
   */
  private withMatchDetails(result: MessageSearchResult): Chat | null {
    const chat = this.chats.find((c) => c.id === result.conversation_id);
    if (!chat) return null;

    return {
      ...chat,
      matchCount: result.matchCount,
      matchingMessageIds: result.matchingMessageIds,
      matchSnippet: result.snippet ?? undefined,
    };
  }

  // Message Function Portion

  /**
   * Function when the user selects the chat of interest in the side bar list items.
   * @param chat - the specific chat item that the user has clicked on from the messages html component
   */
  selectChat(chat: Chat, targetMessageId?: number) {
    this.highlightedMessageId = null;
    this.matchNavDismissed = false;
    const ids = chat.matchingMessageIds ?? [];
    this.currentMatchIndex = targetMessageId
      ? Math.max(0, ids.indexOf(targetMessageId))
      : ids.length - 1;
    this.pendingJumpMessageId = ids[this.currentMatchIndex] ?? null;
    this.chatService.selectChat(chat, this.currentUserId);
  }

  /**
   * The message holding the match the user is currently parked on, so the
   * template can highlight it more strongly than the other matches.
   * @returns {number | null}
   */
  get activeMatchMessageId(): number | null {
    const ids = this.selectedChat?.matchingMessageIds ?? [];
    return ids[this.currentMatchIndex] ?? null;
  }

  /**
   * Hides the match navigation popup. Deliberately leaves the search query and
   * the match results untouched — this only dismisses the UI.
   * @returns {void}
   */
  closeSearchPopup(): void {
    this.matchNavDismissed = true;
  }

  private scrollToMatch(chat: Chat, delta: number): void {
    const ids = chat.matchingMessageIds;
    if (!ids?.length) return;

    //enables wrap around
    this.currentMatchIndex = (this.currentMatchIndex + delta + ids.length) % ids.length;
    this.pendingJumpMessageId = ids[this.currentMatchIndex];

    this.shouldJump = true;
  }

  jumpPrev(chat: Chat): void {
    this.scrollToMatch(chat, -1);
  }

  jumpNext(chat: Chat, targetMessageId?: number): void {
    this.scrollToMatch(chat, 1);
  }

  /** Formats the time to more readable ui for the user */
  formatTime(date: Date | string): string {
    return new Date(date).toLocaleDateString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Returns uppercase initials from a full name string.
   * @param {string} name - Full name to derive initials from.
   * @returns {string} e.g. 'Jean P.' → 'JP'
   */
  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  }

  // Calls on messaging service to send a payload to the socket and emit a sent message towards the recipient
  sendMessage(): void {
    if (this.hasUploadingAttachments) return;

    const readyAttachments = this.pendingAttachments
      .filter((p): p is PendingAttachment & { attachment: MessageAttachment } => !!p.attachment)
      .map((p) => p.attachment);

    this.chatService.sendMessage(
      this.newMessage,
      this.currentUserId,
      readyAttachments.length ? readyAttachments : undefined,
      this.replyingToMessage ?? undefined,
    );
    this.newMessage = '';
    this.pendingAttachments = [];
    this.replyingToMessage = null;
    // Sending always counts as "wanting to be at the bottom" — this message
    // isn't in selectedChat.messages yet (ChatService defers the optimistic
    // append), so the scrolltoBottom() below lands short by exactly this
    // message's height. Flipping isNearBottom here (rather than leaving it
    // at whatever it was before sending) is what makes the live-update
    // branch in the selectedChat$ subscription actually catch up once the
    // message lands, instead of treating it as "arrived while scrolled up,
    // don't yank them down."
    this.isNearBottom = true;
    this.showJumpToLatest = false;
    this.cdr.detectChanges();
    // Instant, not smooth: the deferred live-update correction above lands
    // moments later and would otherwise interrupt this scroll mid-animation
    // with an abrupt instant jump — that exact combination (an in-flight
    // smooth scroll cut off by an instant one) is what caused messages to
    // stop rendering until the user manually touched the scrollbar.
    this.scrolltoBottom(false);

    // Clearing newMessage here is a component-driven model change, so the
    // composer's own (ngModelChange)="sendTyping(...)" binding never fires
    // for it — without this, the recipient's "typing..." would stay stuck
    // on until they typed something themselves.
    this.sendTyping(false);
  }

  /**
   * Triggers the hidden file input. The actual upload happens immediately
   * once a file is selected (onFilesSelected), not deferred to send —
   * mirrors PostDialog's imageHandler() upload-on-select behavior.
   */
  openAttachmentPicker(): void {
    this.attachmentInputRef.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.addFiles(files);
  }

  get canAttachFiles(): boolean {
    if (!this.selectedChat || this.selectedChat.is_disabled) return false;
    return (
      this.userService.currentUser?.hasAnyPermission([this.permissions.MESSAGING.ATTACH_FILES]) ??
      false
    );
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canAttachFiles || event.dataTransfer?.types.includes('Files') === false) return;
    event.preventDefault();
    this.dragDepth++;
    this.isDraggingOver = true;
  }

  onDragOver(event: DragEvent): void {
    if (!this.canAttachFiles) return;
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canAttachFiles) return;
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.isDraggingOver = false;
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.isDraggingOver = false;

    if (!this.canAttachFiles) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.addFiles(files);
  }

  private addFiles(files: File[]): void {
    if (!files.length) return;

    const availableSlots = this.maxAttachments - this.pendingAttachments.length;
    files.slice(0, availableSlots).forEach((file) => this.uploadAttachment(file));
  }

  private uploadAttachment(file: File): void {
    const pending: PendingAttachment = { file, status: 'uploading' };
    this.pendingAttachments = [...this.pendingAttachments, pending];

    this.conversationService.uploadMessageAttachments([file]).subscribe({
      next: ([attachment]) => {
        pending.status = 'done';
        pending.attachment = attachment;
        this.cdr.detectChanges();
      },
      error: () => {
        pending.status = 'error';
        this.cdr.detectChanges();
      },
    });
  }

  removeAttachment(pending: PendingAttachment): void {
    this.pendingAttachments = this.pendingAttachments.filter((p) => p !== pending);
  }

  get canAddMoreAttachments(): boolean {
    return this.pendingAttachments.length < this.maxAttachments;
  }

  /** While true, Send is disabled — sending mid-upload would silently drop
   *  whichever attachment hadn't finished yet, since only ones with a
   *  resolved `.attachment` are bundled into the send. */
  get hasUploadingAttachments(): boolean {
    return this.pendingAttachments.some((p) => p.status === 'uploading');
  }

  isImageAttachment(attachment: MessageAttachment): boolean {
    return attachment.mime_type.startsWith('image/');
  }

  /** A lone photo sizes to its own aspect ratio instead of being forced
   *  into the fixed grid tile used for multi-image messages. */
  isSingleImageMessage(message: Message): boolean {
    return (message.attachments ?? []).filter((a) => this.isImageAttachment(a)).length === 1;
  }

  private isPreviewable(attachment: MessageAttachment): boolean {
    return this.isImageAttachment(attachment) || attachment.mime_type === 'application/pdf';
  }

  getAttachmentIcon(attachment: MessageAttachment): string {
    return attachment.mime_type === 'application/pdf' ? 'picture_as_pdf' : 'insert_drive_file';
  }

  /**
   * Images navigate amongst the other images in the same message; a PDF
   * previews on its own.
   */
  onAttachmentClick(message: Message, attachment: MessageAttachment): void {
    if (!this.isPreviewable(attachment)) {
      window.open(attachment.url, '_blank');
      return;
    }

    if (attachment.mime_type === 'application/pdf') {
      this.dialog.open(AttachmentPreviewDialog, {
        data: { attachments: [attachment], index: 0 },
        panelClass: 'attachment-preview-panel',
        autoFocus: 'dialog',
      });
      return;
    }

    const images = (message.attachments ?? []).filter((a) => this.isImageAttachment(a));
    const index = Math.max(
      images.findIndex((a) => a.url === attachment.url),
      0,
    );

    this.dialog.open(AttachmentPreviewDialog, {
      data: { attachments: images, index },
      panelClass: 'attachment-preview-panel',
      autoFocus: 'dialog',
    });
  }

  /**
   * Notifies the other participant(s) whether this user is currently typing
   * in the currently open conversation.
   * @param {boolean} isTyping - Whether the composer currently has text in it.
   * @returns {void}
   */
  sendTyping(isTyping: boolean): void {
    if (!this.selectedChat) return;

    this.messagingService.sendTyping({
      conversation_id: this.selectedChat.id,
      sender_id: this.currentUserId,
      recipient_id: this.selectedChat.userId,
      isTyping,
    });

    if (isTyping) {
      // Restarts the "gone quiet" debounce timer on every keystroke, so a
      // false is sent automatically a few seconds after the last one.
      this.typingStoppedSubject.next();
    }
  }

  // Calls on Messaging Service to go ahead and delete a message
  deleteMessage(message: Message): void {
    if (!this.selectedChat?.userId) return;

    this.messagingService.deleteMessage(message.id, message.conversation_id);
  }

  ngAfterViewChecked(): void {
    if (this.shouldJump) {
      this.scrollToMessage(this.pendingJumpMessageId);
      this.shouldJump = false;
      this.pendingJumpMessageId = null;
    }

    this.syncMembersPanelBottomSpacing();
  }

  /**
   * Keeps the group-members panel's scrollable list from running past the
   * message input bar. The input bar's rendered height varies (disabled vs
   * enabled state, font/density changes), so it's measured directly via
   * ResizeObserver rather than hardcoded, and mirrored onto the members
   * list as bottom padding.
   */
  private syncMembersPanelBottomSpacing(): void {
    const inputAreaEl = this.inputAreaRef?.nativeElement;

    if (!inputAreaEl || inputAreaEl === this.observedInputAreaEl) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.inputAreaResizeObserver?.disconnect();
    this.observedInputAreaEl = inputAreaEl;

    this.inputAreaResizeObserver = new ResizeObserver(() => {
      const listEl = this.membersPanelListRef?.nativeElement;
      if (listEl) {
        listEl.style.paddingBottom = `${inputAreaEl.offsetHeight}px`;
      }
    });
    this.inputAreaResizeObserver.observe(inputAreaEl);
  }

  /**
   * Scrolls a specific message into view and flags it so the template can
   * highlight it briefly.
   * @param {number | null} messageId - The message to scroll to.
   * @returns {void}
   */
  private scrollToMessage(messageId: number | null): void {
    if (messageId === null) return;

    const element = document.getElementById(`message-${messageId}`);
    if (!element) {
      this.scrolltoBottom();
      return;
    }

    element.scrollIntoView({ block: 'center', behavior: 'smooth' });

    // Clearing first removes the `highlighted` class (needed even when
    // re-jumping to the same message we last highlighted — otherwise
    // re-assigning the same id is a no-op for the class binding). But the
    // removal and re-add below both happen in this same synchronous tick,
    // before the browser ever gets to a paint — so without forcing a
    // reflow in between, the render pipeline never actually observes the
    // "removed" state as a separate step, and a CSS animation tied to the
    // class being added doesn't replay. Reading offsetHeight forces that
    // reflow now, while the class is still off.
    this.highlightedMessageId = null;
    this.cdr.detectChanges();
    void element.offsetHeight;
    this.highlightedMessageId = messageId;
    this.cdr.detectChanges();
  }

  // Incremented right before every programmatic scrollTo()/scrollTop
  // assignment, decremented by the next onMessagesScroll() — see the
  // comment there for why. A counter rather than a single-shot boolean:
  // switching conversations in quick succession can issue several of these
  // before their native `scroll` events (fired asynchronously, one per
  // instant scroll) actually arrive, and a boolean only ever covers the
  // first of them — any later ones would land unprotected and get
  // misread as the user having scrolled away.
  private pendingProgrammaticScrolls = 0;

  // This makes it such that the messages will always display the latest message at the bottom of the conversation selected
  private scrolltoBottom(smooth = true): void {
    try {
      const el = this.scrollContainer.nativeElement;
      this.pendingProgrammaticScrolls++;
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    } catch (e) {
      console.warn('Scroll Failed:', e);
    }
  }

  // Snapshots where the user was scrolled in a conversation right before
  // leaving it, so we can put them back there if they return.
  private saveScrollPosition(chatId: number): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    this.lastScrollPositionByChat.set(chatId, {
      scrollTop: el.scrollTop,
      wasAtBottom: this.isNearBottom,
    });
  }

  private restoreScrollPosition(scrollTop: number): void {
    try {
      const el = this.scrollContainer.nativeElement;
      this.pendingProgrammaticScrolls++; // reuses the existing guard, same as scrolltoBottom()
      el.scrollTop = scrollTop;
    } catch (e) {
      console.warn('Scroll restore failed:', e);
    }
  }

  /**
   * Tracks how far the user has scrolled from the bottom of the open
   * conversation, so new messages only auto-scroll when they were already
   * caught up, and the jump-to-latest button only shows when they aren't.
   *
   * The native `scroll` event this responds to fires asynchronously, so a
   * scrollTo() we just issued ourselves (e.g. jumping to the bottom when a
   * conversation opens) can resolve *after* an attachment has already
   * grown the layout underneath it — landing scrollTop short of the new
   * scrollHeight and making this look like the user scrolled away, when
   * really our own scroll just hadn't caught up yet. One scroll-tracking
   * pass is skipped per outstanding programmatic scroll so those transient
   * snapshots never overwrite isNearBottom.
   * @returns {void}
   */
  onMessagesScroll(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;

    if (this.pendingProgrammaticScrolls > 0) {
      this.pendingProgrammaticScrolls--;
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.isNearBottom = distanceFromBottom < 150;
    this.showJumpToLatest = !this.isNearBottom;
  }

  /**
   * Attachment thumbnails have no height until their image data finishes
   * downloading, so a scrollTo() issued before that can settle short of
   * the true bottom — and unlike a single corrective re-scroll, multiple
   * attachments can each finish loading independently, well after any one
   * correction already ran (e.g. on a slow connection). Re-running the
   * scroll on every image load closes that gap regardless of how many
   * images there are or when they finish. Always instant (not smooth), so
   * it's covered by the same single-shot suppression as every other
   * scrolltoBottom() call above — no separate handling needed. Gated on
   * isNearBottom so a late-loading image from earlier history doesn't
   * yank down a user who has since scrolled up to read.
   * @returns {void}
   */
  onAttachmentImageLoad(): void {
    if (this.isNearBottom) this.scrolltoBottom(false);
  }

  /**
   * Scrolls down to the newest message and dismisses the jump-to-latest button.
   * @returns {void}
   */
  jumpToLatest(): void {
    this.scrolltoBottom(true);
    this.isNearBottom = true;
    this.showJumpToLatest = false;
  }

  /**
   * Returns a date-divider label ("Today", "Yesterday", or a full date) to
   * show above the message at this index, or null if it falls on the same
   * day as the message before it (and so needs no divider).
   * @param {number} index - Index into `selectedChat.messages`.
   * @returns {string | null}
   */
  getDateDividerLabel(index: number): string | null {
    const messages = this.selectedChat?.messages;
    if (!messages?.[index]) return null;

    const current = new Date(messages[index].created_at);
    if (index > 0 && this.isSameDay(current, new Date(messages[index - 1].created_at))) {
      return null;
    }

    return this.formatDateDivider(current);
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private formatDateDivider(date: Date): string {
    const today = new Date();
    if (this.isSameDay(date, today)) return 'Today';

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (this.isSameDay(date, yesterday)) return 'Yesterday';

    return date.toLocaleDateString([], {
      month: 'long',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  }

  /**
   * Confirms with the user before leaving a conversation — leaving is
   * permanent and the button sits right in the chat list, an easy misclick.
   * @param {Chat} chat - The conversation to leave.
   * @returns {void}
   */
  leaveConversation(chat: Chat): void {
    // Direct chats can't really be "left" the way a group can — the backend
    // just hides the conversation until either side sends another message
    // (see leaveConversation in conversation.controller.js). Groups, on the
    // other hand, are actually permanent: leaving posts a system message and
    // there's no auto-return. The dialog copy reflects which one this is.
    const data = chat.is_group
      ? {
          title: 'Leave conversation',
          content: `Are you sure you want to leave "${chat.name}"? You won't see this conversation again.`,
          confirmLabel: 'Leave',
          icon: 'logout',
        }
      : {
          title: 'Hide conversation',
          content: `Hide "${chat.name}"? It'll come back if either of you sends a new message.`,
          confirmLabel: 'Hide',
          icon: 'visibility_off',
        };

    const dialogRef = this.dialog.open(ConfirmCancelDialog, { data });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.chatService.leaveConversation(chat, this.currentUserId);
      }
    });
  }

  // Clean up
  ngOnDestroy(): void {
    // The selected chat and its room membership are deliberately left as-is
    // here — they're shared, session-scoped state (ChatService is a
    // singleton), so navigating away from this page shouldn't clear them.
    // Coming back later, or opening the messaging widget elsewhere in the
    // app, should still show the same conversation, not an empty state.
    this.inputAreaResizeObserver?.disconnect();
    // Separate from the selectedChat/room persistence above — this page's
    // thread view is genuinely gone, so it's no longer on this client's
    // screen via this surface (the widget, if it later becomes visible with
    // the same conversation, sets this again on its own).
    this.messagingService.setVisibleConversation(null);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
